/**
 * Create Container Modal
 *
 * Modal dialog for creating new containers.
 * Uses react-hook-form with Zod validation for robust form handling.
 */

import * as React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { useCreateContainer } from '@/hooks/useContainers';
import { useStorageYards } from '@/hooks/useStorageYards';
import { createContainerSchema, type CreateContainerDTO } from '@/schemas/containerSchema';
import { ApiError } from '@/services/api';

interface CreateContainerModalProps {
  open: boolean;
  onClose: () => void;
}

export function CreateContainerModal({ open, onClose }: CreateContainerModalProps) {
  const createContainer = useCreateContainer();
  const { data: storageYardsData, isLoading: isLoadingYards } = useStorageYards();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateContainerDTO>({
    resolver: zodResolver(createContainerSchema),
    defaultValues: {
      containerNumber: '',
      size: '20Y',
      type: 'rolloff',
      condition: 'good',
      location: {
        type: 'yard',
        address: '',
        yardId: undefined,
      },
      purchaseDate: '',
    },
  });

  // Watch the selected yard ID to auto-populate address
  const selectedYardId = watch('location.yardId');

  // Auto-populate address when yard is selected
  React.useEffect(() => {
    if (selectedYardId && storageYardsData?.yards) {
      const selectedYard = storageYardsData.yards.find(yard => yard.id === selectedYardId);
      if (selectedYard) {
        setValue('location.address', selectedYard.address);
      }
    }
  }, [selectedYardId, storageYardsData, setValue]);

  const onSubmit = async (data: CreateContainerDTO) => {
    try {
      await createContainer.mutateAsync(data);
      reset();
      onClose();
    } catch (error) {
      // Handle API validation errors with field-level details
      if (error instanceof ApiError && error.details && error.details.length > 0) {
        error.details.forEach((detail) => {
          setError(detail.field as keyof CreateContainerDTO, {
            type: 'server',
            message: detail.message,
          });
        });
      }
      // Generic error toast handled by mutation
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Container</DialogTitle>
          <DialogDescription>
            Add a new container to your fleet. All fields marked with * are required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Container Number */}
          <div className="space-y-2">
            <label
              htmlFor="containerNumber"
              className="block text-sm font-medium text-gray-900"
            >
              Container Number <span className="text-red-600">*</span>
            </label>
            <input
              {...register('containerNumber')}
              id="containerNumber"
              type="text"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="e.g., C-1001"
            />
            {errors.containerNumber && (
              <p className="text-sm text-red-600">{errors.containerNumber.message}</p>
            )}
          </div>

          {/* Size and Type Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Size */}
            <div className="space-y-2">
              <label htmlFor="size" className="block text-sm font-medium text-gray-900">
                Size <span className="text-red-600">*</span>
              </label>
              <select
                {...register('size')}
                id="size"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="10Y">10 Yard</option>
                <option value="20Y">20 Yard</option>
                <option value="30Y">30 Yard</option>
                <option value="40Y">40 Yard</option>
              </select>
              {errors.size && (
                <p className="text-sm text-red-600">{errors.size.message}</p>
              )}
            </div>

            {/* Type */}
            <div className="space-y-2">
              <label htmlFor="type" className="block text-sm font-medium text-gray-900">
                Type <span className="text-red-600">*</span>
              </label>
              <select
                {...register('type')}
                id="type"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="rolloff">Roll-off</option>
                <option value="compactor">Compactor</option>
                <option value="stationary">Stationary</option>
              </select>
              {errors.type && (
                <p className="text-sm text-red-600">{errors.type.message}</p>
              )}
            </div>
          </div>

          {/* Condition */}
          <div className="space-y-2">
            <label htmlFor="condition" className="block text-sm font-medium text-gray-900">
              Condition <span className="text-red-600">*</span>
            </label>
            <select
              {...register('condition')}
              id="condition"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="excellent">Excellent</option>
              <option value="good">Good</option>
              <option value="fair">Fair</option>
              <option value="poor">Poor</option>
              <option value="needs_repair">Needs Repair</option>
            </select>
            {errors.condition && (
              <p className="text-sm text-red-600">{errors.condition.message}</p>
            )}
          </div>

          {/* Location Section */}
          <div className="space-y-4 border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-900">Container Location</h3>

            {/* Location Type */}
            <div className="space-y-2">
              <label htmlFor="locationType" className="block text-sm font-medium text-gray-900">
                Location Type <span className="text-red-600">*</span>
              </label>
              <select
                {...register('location.type')}
                id="locationType"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="yard">Storage Yard</option>
                <option value="job_site">Job Site</option>
                <option value="in_transit">In Transit</option>
              </select>
              {errors.location?.type && (
                <p className="text-sm text-red-600">{errors.location.type.message}</p>
              )}
            </div>

            {/* Storage Yard Selector (conditional on location type) */}
            {watch('location.type') === 'yard' && (
              <div className="space-y-2">
                <label htmlFor="yardId" className="block text-sm font-medium text-gray-900">
                  Storage Yard <span className="text-red-600">*</span>
                </label>
                {isLoadingYards ? (
                  <div className="text-sm text-gray-500">Loading storage yards...</div>
                ) : !storageYardsData?.yards?.length ? (
                  <div className="rounded-md bg-yellow-50 border border-yellow-200 p-3">
                    <p className="text-sm text-yellow-800">
                      No storage yards found. Please create a storage yard first before creating containers.
                    </p>
                  </div>
                ) : (
                  <>
                    <Controller
                      name="location.yardId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          id="yardId"
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value || undefined)}
                        >
                          <option value="">Select a storage yard...</option>
                          {storageYardsData.yards.map((yard) => (
                            <option key={yard.id} value={yard.id}>
                              {yard.name} {yard.isDefault ? '(Default)' : ''}
                            </option>
                          ))}
                        </Select>
                      )}
                    />
                    {errors.location?.yardId && (
                      <p className="text-sm text-red-600">{errors.location.yardId.message}</p>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Location Address (auto-populated for yards, manual for others) */}
            <div className="space-y-2">
              <label htmlFor="locationAddress" className="block text-sm font-medium text-gray-900">
                Location Address <span className="text-red-600">*</span>
              </label>
              <textarea
                {...register('location.address')}
                id="locationAddress"
                rows={2}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder={
                  watch('location.type') === 'yard'
                    ? 'Select a storage yard to auto-populate address'
                    : '123 Main St, City, State 12345'
                }
                readOnly={watch('location.type') === 'yard' && !!selectedYardId}
              />
              {errors.location?.address && (
                <p className="text-sm text-red-600">{errors.location.address.message}</p>
              )}
              {watch('location.type') === 'yard' && selectedYardId && (
                <p className="text-xs text-gray-500">
                  Address auto-populated from selected storage yard
                </p>
              )}
            </div>
          </div>

          {/* Purchase Date */}
          <div className="space-y-2">
            <label htmlFor="purchaseDate" className="block text-sm font-medium text-gray-900">
              Purchase Date <span className="text-gray-500 text-xs">(optional)</span>
            </label>
            <input
              {...register('purchaseDate')}
              id="purchaseDate"
              type="date"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            {errors.purchaseDate && (
              <p className="text-sm text-red-600">{errors.purchaseDate.message}</p>
            )}
          </div>

          {/* Footer Actions */}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Container'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
